import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  forwardRef,
  inject,
  Input,
  OnInit,
  signal,
  viewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { MAT_AUTOCOMPLETE_DEFAULT_OPTIONS, MatAutocompleteModule, MatAutocompleteTrigger } from "@angular/material/autocomplete";
import { ActivatedRoute, NavigationExtras, NavigationStart, Router } from "@angular/router";

import { filter } from "rxjs/operators";
import { EntryResult, OrganizationId } from "src/core/backend/controllers";

// replaced AutoUnsubscribe usage with takeUntilDestroyed
import { isNotNull } from "#shared/utils";

import { SharedModule } from "#modules/shared/shared.module";

import { QuickSearchService } from "../../services/quick-search.service";
import { SearchResultComponent } from "../search-result/search-result.component";
import { ItemLinkPipe } from "./item-link.pipe";

@Component({
  selector: "app-quick-search",
  templateUrl: "./quick-search.component.html",
  styleUrls: ["./quick-search.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => QuickSearchComponent),
    },
    {
      provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
      useValue: {
        // Assign the string or array of strings matching your global CSS
        overlayPanelClass: "custom-autocomplete-overlay",
        autoActiveFirstOption: true,
      },
    },
  ],
  imports: [MatAutocompleteModule, SharedModule, SearchResultComponent, ItemLinkPipe],
})
export class QuickSearchComponent implements OnInit {
  private readonly quickSearchService = inject(QuickSearchService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  readonly inputEl = viewChild.required<ElementRef<HTMLInputElement>>("input");
  readonly trigger = viewChild.required(MatAutocompleteTrigger);

  readonly isEmpty$ = this.quickSearchService.isEmpty$;
  readonly loading = this.quickSearchService.loading;
  readonly searchString$ = this.quickSearchService.input$;

  readonly searchResults$ = this.quickSearchService.results$;

  @Input() organization: OrganizationId | undefined | null;

  // The search field is permanently open (schalter-e style); `expanded` is kept
  // so the collapsible behaviour can be re-enabled per breakpoint if needed.
  readonly expanded = signal(true);

  ngOnInit(): void {
    /** Close autocomplete if sub link was clicked */
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.clear();
        this.trigger().closePanel();
      });
  }

  onInput(value: string) {
    this.searchString$.next(value);
  }

  onClear() {
    this.clear();
  }

  onSearchClick(event: MouseEvent) {
    event.stopPropagation();
    if (this.expanded()) {
      if (this.searchString$.getValue()?.trim().length) {
        this.onEnter();
      } else {
        this.focusInputEl();
      }
    } else {
      this.expanded.set(true);
    }
  }

  private focusInputEl() {
    this.inputEl().nativeElement.focus();
  }

  onEnter(trigger?: MatAutocompleteTrigger) {
    const anchor = trigger?.activeOption?._getHostElement().querySelector("a");
    if (anchor) {
      anchor.click();
    } else {
      this.navigate();
    }
  }

  private clear() {
    this.searchString$.next("");
  }

  private navigate(item?: EntryResult) {
    const search = this.searchString$.getValue()?.trim();
    const extrasBase: NavigationExtras = { relativeTo: this.route, queryParams: { search } };

    if (!item) {
      this.router.navigate(["/search"].filter(isNotNull), extrasBase);
    } else {
      const itemLink = new ItemLinkPipe().transform(item);
      this.router.navigate(itemLink.routerLink, { ...extrasBase, queryParams: { ...extrasBase.queryParams, ...itemLink.queryParams } });
    }
  }

  onTransitionStart(event: TransitionEvent) {
    if (event.propertyName === "width" && !this.expanded()) {
      this.trigger()?.closePanel();
      this.inputEl().nativeElement.blur();
    }
  }

  onTransitionEnd(event: TransitionEvent) {
    if (event.propertyName === "width" && this.expanded()) {
      // No focus here: a width transition also fires on toolbar reflow, which
      // would open the suggestion panel by itself on page load.
      this.trigger().updatePosition();
    }
  }

  onInputBlur(event: FocusEvent) {
    // The field stays open on blur; only the suggestion panel closes (handled
    // by the autocomplete itself).
  }
}
